/**
 * Production reporting for Next.js client error boundaries (segment + global).
 * - Structured logs: POST /api/client-error (server console / log sinks).
 * - Optional Sentry: set NEXT_PUBLIC_SENTRY_DSN.
 */

export type ClientBoundaryErrorCategory =
  | 'chunk_load'
  | 'hydration'
  | 'network'
  | 'runtime'
  | 'unknown';

export type ClientBoundarySource = 'segment' | 'global';

export function inferClientBoundaryErrorCategory(error: Error): ClientBoundaryErrorCategory {
  const msg = (error.message || '').toLowerCase();
  const name = error.name || '';

  if (
    name === 'ChunkLoadError' ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed')
  ) {
    return 'chunk_load';
  }
  if (
    msg.includes('hydration') ||
    msg.includes('did not match') ||
    (msg.includes('hydrate') && (msg.includes('html') || msg.includes('server')))
  ) {
    return 'hydration';
  }
  if (
    msg.includes('failed to fetch') ||
    (name === 'TypeError' && msg.includes('fetch')) ||
    name === 'NetworkError' ||
    msg.includes('load failed') ||
    msg.includes('networkerror')
  ) {
    return 'network';
  }
  if (['TypeError', 'ReferenceError', 'RangeError', 'SyntaxError'].includes(name)) {
    return 'runtime';
  }
  return 'unknown';
}

let sentryClientInitStarted = false;

async function captureWithSentry(
  error: Error,
  ctx: { pathname: string; category: ClientBoundaryErrorCategory; digest?: string; source: ClientBoundarySource },
): Promise<void> {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn || typeof window === 'undefined') return;

  try {
    const Sentry = await import('@sentry/react');
    if (!sentryClientInitStarted) {
      Sentry.init({
        dsn,
        environment: process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.NODE_ENV,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
      });
      sentryClientInitStarted = true;
    }
    Sentry.captureException(error, {
      tags: {
        error_boundary: ctx.source,
        error_category: ctx.category,
      },
      extra: {
        digest: ctx.digest,
        pathname: ctx.pathname,
      },
    });
  } catch {
    // Optional dependency path — never break the error UI
  }
}

function postStructuredReport(body: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    const json = JSON.stringify(body);
    void fetch('/api/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

/**
 * Fire-and-forget: logs to server + optional Sentry. Safe to call from error boundary effects.
 */
export function reportClientBoundaryError(
  error: Error & { digest?: string },
  source: ClientBoundarySource = 'segment',
): void {
  if (process.env.NODE_ENV !== 'production') return;

  const pathname = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  const category = inferClientBoundaryErrorCategory(error);

  postStructuredReport({
    digest: error.digest,
    pathname,
    category,
    name: error.name,
    message: (error.message || '').slice(0, 2000),
    stack: error.stack?.slice(0, 12000),
    source,
    reportedAt: new Date().toISOString(),
  });

  void captureWithSentry(error, {
    pathname,
    category,
    digest: error.digest,
    source,
  });
}
